/**
 * This file contains utility functions for activity logs.
 * It is used to display the activity log rows in the order detail page.
 */
import { ActivityLogFieldsFragment } from 'src/types';
import { ActivityAction, ActivityResourceType } from 'src/types/activtiy_logs';
import { getEnumValue } from 'src/utils/enum_utils';

// Define types for activity log details and actions
interface ActivityLogDetails {
  updatedFields?: string[];
  isRevisedReport?: boolean;
  filename?: string;
  category?: string;
  recipients?: string[];
  [key: string]: unknown;
}

// Field order for sorting updated fields
const fieldOrder = [
  'Patient ID',
  'Age at Diagnosis',
  'NCCN Risk Category',
  'Primary Gleason Score',
  'Secondary Gleason Score',
  'Baseline PSA',
  'T-Stage',
  'N-Stage',
  'Tumor Size',
];

// Map internal field names to display names
const mapFieldName = (fieldName: string): string => {
  const fieldNameMap: Record<string, string> = {
    externalId: 'Patient ID',
    patientAge: 'Age at Diagnosis',
    nccnRiskCategory: 'NCCN Risk Category',
    primaryGleasonScore: 'Primary Gleason Score',
    secondaryGleasonScore: 'Secondary Gleason Score',
    psa: 'Baseline PSA',
    tStage: 'T-stage',
    nStage: 'N-Stage',
    tumorSize: 'Tumor Size',
  };

  return fieldNameMap[fieldName] || fieldName;
};

// Returns a human readable message for the updated fields.
const parseUpdatedFields = (updatedFields: string[]): string => {
  const mappedUpdatedFields = updatedFields.map(mapFieldName).sort((a, b) => {
    const indexA = fieldOrder.indexOf(a);
    const indexB = fieldOrder.indexOf(b);
    return indexA - indexB;
  });

  if (mappedUpdatedFields.length === 1) {
    return mappedUpdatedFields[0];
  } else if (mappedUpdatedFields.length === 2) {
    return `${mappedUpdatedFields[0]} and ${mappedUpdatedFields[1]}`;
  }

  const lastField = mappedUpdatedFields[mappedUpdatedFields.length - 1];
  const otherFields = mappedUpdatedFields.slice(0, -1).join(', ');

  return `${otherFields}, and ${lastField}`;
};

const getOrderActivityLogMessage = (
  action: ActivityAction,
  details?: ActivityLogDetails | null,
): string => {
  switch (action) {
    case ActivityAction.CREATE:
      return 'created this order draft';
    case ActivityAction.UPDATE:
      return 'edited this order';
    case ActivityAction.QC_UPDATE:
      if (!details?.updatedFields || details.updatedFields.length === 0) {
        return 'edited this order';
      }
      return `updated ${parseUpdatedFields(details.updatedFields)}`;
    case ActivityAction.SUBMIT:
      return 'submitted this order';
    case ActivityAction.RESUBMIT:
      return 'resubmitted this order';
    case ActivityAction.REVERT:
      return 'A Customer Success user reverted this order to Quality Control Pending';
    case ActivityAction.FAX_PATHOLOGY_FILES:
      return 'faxed the pathology files to the pathology lab';
    case ActivityAction.CANCEL:
      return 'canceled this order';
    case ActivityAction.FAXED_BILLING_FILES:
      return 'faxed the billing files';
    case ActivityAction.MARK_QC_APPROVED:
      return 'approved this order for quality control';
    case ActivityAction.MARK_QC_REJECTED:
      return 'requested additional information for this order';
    case ActivityAction.MARK_LAB_PROCESSING:
      return 'marked this order as Lab Processing';
    case ActivityAction.MARK_REPORT_PENDING:
      return 'The test report for this order is pending release';
    case ActivityAction.MARK_AI_ERROR:
      return 'An AI error occurred';
    case ActivityAction.MARK_LAB_ERROR:
      return 'A lab error occurred';
    case ActivityAction.MARK_REPORT_READY:
      return 'The test report for this order is ready';
    case ActivityAction.MARK_REPORT_FAXED: {
      const showRevisedReportFaxed = details?.isRevisedReport === true;
      return showRevisedReportFaxed
        ? 'faxed the revised report to the order creator'
        : 'faxed the report to the order creator';
    }
    case ActivityAction.MARK_REPORT_REJECTED:
      return 'An error occurred while generating the report';
    case ActivityAction.MARK_TRF_FAXED:
      return 'faxed the TRF to the pathology lab';
    case ActivityAction.MARK_AI_PROCESSING:
      return 'This order is in AI processing';
    case ActivityAction.FAX_TEST_REPORT_ORDERING_PROVIDER:
      return 'faxed the test report to the ordering provider';
    case ActivityAction.FAX_TEST_REPORT_PATHOLOGY_LAB:
      return 'faxed the test report to the pathology lab';
    case ActivityAction.UPLOAD_FILE:
      if (details?.filename && details?.category) {
        return `uploaded ${details.filename} as ${details.category}`;
      }
      return 'uploaded a file';
    case ActivityAction.DELETE_FILE:
      if (details?.filename && details?.category) {
        return `deleted the file ${details.filename} from ${details.category}`;
      }
      return 'deleted a file';
    case ActivityAction.REVISE_REPORT:
      return 'requested report revision for this order';
    case ActivityAction.SHARE_TEST_REPORT:
      if (details?.recipients) {
        return `shared the test report to: ${details.recipients.join(', ')}`;
      }
      return 'shared the test report';
    case ActivityAction.QC_APPROVE_SLIDE_IMAGE:
      return 'approved the slide image for quality control';
    case ActivityAction.QC_REJECT_SLIDE_IMAGE:
      return 'rejected the slide image for quality control';
  }
};

const getOrderingProviderActivityLogMessage = (
  action?: string | null,
  _details?: ActivityLogDetails | null,
): string => {
  switch (action) {
    case ActivityAction.CREATE:
      return 'created an ordering provider';
    case ActivityAction.UPDATE:
      return "updated the ordering provider's information";
    default:
      return '';
  }
};

export const getActivityLogMessage = (
  activityLog: ActivityLogFieldsFragment,
): string => {
  const details = activityLog.details
    ? (JSON.parse(activityLog.details as string) as ActivityLogDetails)
    : null;
  const action = getEnumValue(ActivityAction, activityLog.action!);

  switch (activityLog.resourceType) {
    case ActivityResourceType.ORDERING_PROVIDER:
      return getOrderingProviderActivityLogMessage(action, details);
    case ActivityResourceType.ORDER:
      return getOrderActivityLogMessage(action, details);
    default:
      return '';
  }
};
